import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
    Search, Home, MapPin, Wallet, BedDouble, Building2, Users, Loader2,
    Target, Phone, Mail, ChevronDown, ChevronUp, ChevronRight, Check, X, Copy, Download,
    Compass, Bath, Pencil, MessageSquare, Send,
    ImagePlus, Trash2, ShieldCheck, Eye, EyeOff,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
    fetchFindRentersListings,
    fetchFindRentersSuburbs,
    findRentersForProperty,
    editRenterPreferences,
    messageRenterMatches,
} from '../api/analyticsApi';
import { PropertyTypeTiles, SuburbPicker } from './BuyerPreferenceFields';
import SuburbAutocomplete from '../../../components/common/SuburbAutocomplete';
import { smsInfo, asSent } from '../../communications/utils/smsCredits';
import api from '../../../api';
import { prettyGnafAddress } from '../../../components/common/AddressAutocomplete';
import { geocodePlace, enrichSuburbsWithDistance, hasGeocoder } from '../utils/geocode';
import { hasAdminAccess } from "../../../utils/roleAccess";
import { escapeHtml } from '../../communications/utils/messageBlocks';
import { sanitizeHtml } from '../../../utils/sanitizeHtml';

// Our own GNAF address search, the same source Find Buyers uses. (This used to call
// Addressr on RapidAPI directly, which shipped its key in the browser bundle.)
const searchAddresses = async (query) => {
    if (!query || query.length < 3) return [];
    try {
        const res = await api.get('/api/suburb-intelligence/address-autocomplete/', { params: { q: query } });
        const results = Array.isArray(res?.data?.results) ? res.data.results : [];
        return results.filter((r) => r && r.address).slice(0, 6).map((r) => ({
            description: prettyGnafAddress(r.address),
            raw: { gnaf: true, property_id: r.property_id },
        }));
    } catch {
        return [];
    }
};

const AU_STATES = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'];

const parseAddressString = (s) => {
    const str = (s || '').trim();
    let suburb = '', state = '', postcode = '';
    const pc = str.match(/\b(\d{4})\b(?![\s\S]*\b\d{4}\b)/);
    if (pc) postcode = pc[1];

    const stateRe = new RegExp(`\\b(${AU_STATES.join('|')})\\b`, 'ig');
    let m, lastState = null;
    while ((m = stateRe.exec(str)) !== null) lastState = m;
    if (lastState) {
        state = lastState[1].toUpperCase();
        const before = str.slice(0, lastState.index).replace(/,\s*$/, '').trim();
        suburb = before.split(',').pop().trim();
    } else if (str.includes(',')) {
        const parts = str.split(',').map((x) => x.trim()).filter(Boolean);
        if (parts.length >= 2) suburb = parts[parts.length - 1];
    }
    return { suburb, state, postcode };
};

const extractAddressFields = (sugg) => {
    const raw = sugg?.raw || {};
    const comp = raw.components || raw.address_components || {};
    let suburb = raw.suburb || raw.locality || raw.locality_name || raw.suburb_name || comp.suburb || comp.locality || '';
    let state = raw.state || raw.state_abbreviation || raw.state_code || raw.state_name || comp.state || '';
    let postcode = raw.postcode || raw.postal_code || comp.postcode || comp.postal_code || '';
    const lat = raw.latitude ?? raw.lat ?? raw.location?.lat ?? raw.geo?.lat ?? raw.geocoding?.latitude ?? null;
    const lng = raw.longitude ?? raw.lng ?? raw.lon ?? raw.location?.lon ?? raw.location?.lng ?? raw.geo?.lng ?? raw.geocoding?.longitude ?? null;
    if (!suburb || !state || !postcode) {
        const p = parseAddressString(sugg?.description || '');
        suburb = suburb || p.suburb; state = state || p.state; postcode = postcode || p.postcode;
    }
    return {
        suburb, state, postcode,
        lat: lat != null ? Number(lat) : null,
        lng: lng != null ? Number(lng) : null,
    };
};

/**
 * Find Renters for a Property (Renter Match tab on a lease listing)
 *
 * The lease-listing sibling of FindBuyersForProperty.js (Monday 12908991007,
 * studios@collings.com.au, 2026-08-27): "we need to add an option to share
 * off and on market rental properties to renters that have enquired
 * regarding other properties... add 'renter match' to identify other
 * renters to send the property to."
 *
 * v1: renters come from ONE source (RentingPreference), not the buy side's
 * three databases — there's no renter equivalent yet of the CRM requirements
 * model or the imported buyer database. No PDF report and no "send test
 * message" yet either (deferred; the latter isn't even wired up on the buy
 * side today). The core loop — pick a listing, tick suburbs, rank renters,
 * email/text the matches — is the same shape as Find Buyers throughout.
 */

const RADIUS_OPTIONS = [2, 5, 10, 15, 25];
const ROWS_PER_BATCH = 100;

const LISTING_TYPE_TO_OPTION = {
    house: 'House', apartment: 'Apartment', unit: 'Unit', townhouse: 'Townhouse',
    villa: 'Villa', land: 'Land', blocks_of_units: 'Block of Units', house_and_land: 'House and Land',
};

const fmtMoney = (n) => {
    if (n == null || n === '') return '';
    const num = Number(n);
    if (Number.isNaN(num)) return '';
    return `$${num.toLocaleString('en-AU')}`;
};

const SUBURB_KIND = {
    exact: { label: 'This suburb', chip: 'bg-emerald-100 text-emerald-700' },
    surrounding: { label: 'Surrounding', chip: 'bg-sky-100 text-sky-700' },
    other: { label: 'Other areas', chip: 'bg-gray-100 text-gray-600' },
};

const MATCH_SUBURB_META = {
    exact: { text: 'text-emerald-700', bg: 'bg-emerald-50', ring: 'border-emerald-200' },
    surrounding: { text: 'text-sky-700', bg: 'bg-sky-50', ring: 'border-sky-200' },
    other: { text: 'text-gray-600', bg: 'bg-gray-50', ring: 'border-gray-200' },
};

const FB_META = {
    hot: { label: 'Loved it', cls: 'text-orange-700 bg-orange-100' },
    warm: { label: 'Interested', cls: 'text-sky-700 bg-sky-100' },
    cold: { label: 'Lukewarm', cls: 'text-gray-500 bg-gray-100' },
    none: { label: 'Not keen', cls: 'text-gray-500 bg-gray-100' },
};
const fbBucket = (fb) => {
    const il = (fb?.interest_level || '').toLowerCase();
    if (['hot', 'very_high', 'high'].includes(il)) return 'hot';
    if (['warm', 'medium'].includes(il)) return 'warm';
    if (['cold', 'low'].includes(il)) return 'cold';
    if (il === 'none') return 'none';
    return fb?.text ? 'warm' : null;
};

const rowId = (r) => `rent_portal:${r.ref_id}`;

const scoreColor = (s) => {
    if (s >= 80) return 'text-emerald-600';
    if (s >= 60) return 'text-sky-600';
    if (s >= 40) return 'text-amber-600';
    return 'text-gray-500';
};

const RangeField = ({ icon: Icon, label, minVal, maxVal, onMin, onMax, phMin = 'Min', phMax = 'Max', step }) => (
    <div className="text-xs text-gray-500">
        <span className="flex items-center gap-1 mb-1">{Icon && <Icon className="w-3 h-3" />} {label}</span>
        <div className="flex items-center gap-1.5">
            <input type="number" step={step} value={minVal} onChange={(e) => onMin(e.target.value)}
                className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm text-gray-900" placeholder={phMin} />
            <span className="text-gray-300">to</span>
            <input type="number" step={step} value={maxVal} onChange={(e) => onMax(e.target.value)}
                className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm text-gray-900" placeholder={phMax} />
        </div>
    </div>
);

const FitChip = ({ ok, near, label }) => {
    const cls = ok
        ? 'bg-emerald-50 text-emerald-700'
        : near
            ? 'bg-amber-50 text-amber-700'
            : 'bg-gray-100 text-gray-500';
    return <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] ${cls}`}>{label}</span>;
};

const prefNum = (v) => {
    if (v == null) return '';
    const s = String(v).replace(/[^0-9.]/g, '');
    return s || '';
};

// Edit a matched renter's saved preferences — always writes to RentingPreference.
function EditRenterModal({ renter, onClose, onSaved }) {
    const [form, setForm] = useState(() => ({
        suburbs: renter.pref_suburbs || [],
        rentMin: prefNum(renter.pref_rent_min), rentMax: prefNum(renter.pref_rent_max),
        bedsMin: prefNum(renter.pref_bedrooms_min), bedsMax: prefNum(renter.pref_bedrooms_max),
        baths: prefNum(renter.pref_bathrooms),
        types: renter.pref_property_type || [],
    }));
    const [saving, setSaving] = useState(false);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const num = (v) => (v !== '' ? Number(v) : null);

    const save = async () => {
        try {
            setSaving(true);
            await editRenterPreferences({
                ref_id: renter.ref_id,
                suburbs: form.suburbs,
                rent_min: num(form.rentMin), rent_max: num(form.rentMax),
                beds_min: num(form.bedsMin), beds_max: num(form.bedsMax),
                baths: num(form.baths),
                property_types: form.types,
            });
            toast.success('Preferences updated');
            onSaved && onSaved();
        } catch {
            toast.error('Could not save preferences');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onMouseDown={onClose}>
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[92vh] flex flex-col" onMouseDown={(e) => e.stopPropagation()}>
                <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-gray-100">
                    <div className="flex items-center gap-2 min-w-0">
                        <Pencil className="w-4 h-4 text-teal-600 shrink-0" />
                        <span className="text-sm font-semibold text-gray-900 truncate">Edit preferences · {renter.name || renter.email || 'Renter'}</span>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    <div>
                        <span className="block text-xs font-semibold text-gray-600 mb-1.5">Property types</span>
                        <PropertyTypeTiles value={form.types} onChange={(v) => set('types', v)} />
                    </div>
                    <div>
                        <span className="block text-xs font-semibold text-gray-600 mb-1.5">Suburbs</span>
                        <SuburbPicker value={form.suburbs} onChange={(v) => set('suburbs', v)} />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <RangeField icon={Wallet} label="Weekly rent ($)" step="10"
                            minVal={form.rentMin} maxVal={form.rentMax}
                            onMin={(v) => set('rentMin', v)} onMax={(v) => set('rentMax', v)} />
                        <RangeField icon={BedDouble} label="Bedrooms"
                            minVal={form.bedsMin} maxVal={form.bedsMax}
                            onMin={(v) => set('bedsMin', v)} onMax={(v) => set('bedsMax', v)} />
                    </div>
                    <label className="text-xs text-gray-500 block w-1/2 pr-1.5">
                        <span className="flex items-center gap-1 mb-1"><Bath className="w-3 h-3" /> Bathrooms (min)</span>
                        <input type="number" value={form.baths} onChange={(e) => set('baths', e.target.value)}
                            className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm text-gray-900" placeholder="Any" />
                    </label>
                </div>
                <div className="flex-shrink-0 flex items-center justify-end gap-2 px-4 py-3 border-t border-gray-100">
                    <button onClick={onClose} className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
                    <button onClick={save} disabled={saving}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-teal-600 text-white text-sm font-semibold hover:bg-teal-700 disabled:opacity-50">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save
                    </button>
                </div>
            </div>
        </div>
    );
}

const UNSUBSCRIBE_BUTTON_HTML =
    '<p style="margin:28px 0 0;text-align:center">'
    + '<a href="%%UNSUBSCRIBE_URL%%" style="display:inline-block;padding:8px 18px;border:1px solid #d1d5db;'
    + 'border-radius:6px;color:#6b7280;font-size:12px;text-decoration:none">Unsubscribe</a></p>';

const MAX_IMAGE_BYTES = 3 * 1024 * 1024;

const previewText = (text, firstName) => (text || '')
    .replace(/\{\{\s*FirstName\s*\}\}/g, firstName || 'there')
    .replace(/\{\{\s*Name\s*\}\}/g, firstName || 'there')
    .replace(/\{\{\s*PreferencesLink\s*\}\}/g, 'your preferences link');

const PRICING_STANDINS = {
    FirstName: 'Samantha',
    Name: 'Samantha Nguyen',
    PreferencesLink:
        'https://crm.collings.com.au/buyer-portal/preferences/?token=' + 'x'.repeat(230),
};

const pricedText = (text) => (text || '').replace(
    /\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g,
    (whole, tag) => (Object.prototype.hasOwnProperty.call(PRICING_STANDINS, tag)
        ? PRICING_STANDINS[tag] : whole),
);

const previewHtml = (html, firstName) => (html || '')
    .replace(/\{\{\s*FirstName\s*\}\}/g, escapeHtml(firstName || 'there'))
    .replace(/\{\{\s*Name\s*\}\}/g, escapeHtml(firstName || 'there'))
    .replace(/\{\{\s*PreferencesLink\s*\}\}/g, '#')
    .replace(/%%UNSUBSCRIBE_URL%%/g, '#');

export function MessageRenterMatchesModal({ channel, recipients, propertyLabel, onClose, onSent }) {
    const isEmail = channel === 'email';
    const label = (propertyLabel || 'this property').trim();

    const withEmail = recipients.filter((r) => r.email).length;
    const withPhone = recipients.filter((r) => r.phone).length;
    const reachable = isEmail ? withEmail : withPhone;

    const [subject, setSubject] = useState(
        isEmail ? `A rental that matches your search: ${label}` : ''
    );
    const [body, setBody] = useState(() =>
        isEmail
            ? `Hi {{FirstName}},\n\nA rental property has just come up that matches what you're looking for: ${label}.\n\n`
              + `Would you like to arrange an inspection? Just reply to this email or give me a call.\n\n`
              + `If this doesn't match your current requirements, please click the link below to update your `
              + `preferences so we can ensure you receive properties that are relevant to what you're looking for.\n`
              + `<a href="{{PreferencesLink}}">Update my preferences</a>\n\nKind regards,\nCollings Real Estate`
            : `Hi {{FirstName}}, a rental matching your search just came up: ${label}. Keen for a look? Reply or call us.`
    );
    const [image, setImage] = useState(null);
    const [sending, setSending] = useState(false);
    const [showPreview, setShowPreview] = useState(true);
    const fileRef = useRef(null);

    const toHtml = (text) => text.replace(/\n/g, '<br>');

    const emailHtml = () => {
        const parts = [toHtml(body)];
        if (image) {
            parts.push(
                `<p style="margin:16px 0"><img src="${image.dataUrl}" alt="" `
                + 'style="max-width:100%;height:auto;border-radius:8px" /></p>'
            );
        }
        parts.push(UNSUBSCRIBE_BUTTON_HTML);
        return parts.join('');
    };

    const smsText = () => body.trim();

    const sms = smsInfo(asSent(pricedText(smsText())));
    const totalCredits = sms.credits * reachable;

    const pickImage = (e) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = '';
        if (!file) return;
        if (!/^image\//.test(file.type)) { toast.error('Pick an image file.'); return; }
        if (file.size > MAX_IMAGE_BYTES) { toast.error('Images need to be under 3MB.'); return; }
        const reader = new FileReader();
        reader.onload = () => setImage({ name: file.name, dataUrl: String(reader.result), size: file.size });
        reader.onerror = () => toast.error('Could not read that image.');
        reader.readAsDataURL(file);
    };

    const send = async () => {
        if (!body.trim()) { toast.error('Add a message first.'); return; }
        if (isEmail && !subject.trim()) { toast.error('Add a subject first.'); return; }
        if (reachable === 0) {
            toast.error(isEmail ? 'None of these renters have an email.' : 'None of these renters have a phone number.');
            return;
        }
        try {
            setSending(true);
            const res = await messageRenterMatches({
                channel,
                subject: isEmail ? subject.trim() : '',
                body: isEmail ? emailHtml() : smsText(),
                recipients: recipients.map((r) => ({
                    ref_id: r.ref_id, contact_id: r.contact_id,
                    email: r.email, phone: r.phone, name: r.name,
                })),
            });
            const noun = isEmail ? 'email' : 'text';
            if (res.simulated) {
                toast.success(
                    `Simulated ${res.reachable ?? reachable} ${noun}s, sending is currently switched off (nothing left the building).`,
                    { duration: 6000 }
                );
            } else if (res.queued) {
                toast.success(
                    `Sending ${(res.reachable ?? reachable).toLocaleString()} ${noun}s in the background. `
                    + 'Large lists can take a few minutes.',
                    { duration: 6000 }
                );
            } else {
                const sent = res.sent || 0;
                const skipped = res.skipped || 0;
                toast.success(`Sent ${sent} ${noun}${sent !== 1 ? 's' : ''}${skipped ? ` · ${skipped} skipped (opted out, no address or already received it)` : ''}.`);
            }
            onSent && onSent();
        } catch {
            toast.error('Could not send the messages.');
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onMouseDown={onClose}>
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[92vh] flex flex-col" onMouseDown={(e) => e.stopPropagation()}>
                <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-gray-100">
                    <div className="flex items-center gap-2 min-w-0">
                        {isEmail ? <Mail className="w-4 h-4 text-indigo-600 shrink-0" /> : <MessageSquare className="w-4 h-4 text-indigo-600 shrink-0" />}
                        <span className="text-sm font-semibold text-gray-900 truncate">
                            {isEmail ? 'Email' : 'Text'} {recipients.length.toLocaleString()} matched renter{recipients.length !== 1 ? 's' : ''}
                        </span>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                    <p className="text-xs text-gray-500">
                        {reachable.toLocaleString()} of {recipients.length.toLocaleString()} {reachable === 1 ? 'has' : 'have'} {isEmail ? 'an email address' : 'a phone number'} and will be contacted.
                        {reachable !== recipients.length && ' The rest are skipped.'} Renters who opted out are always skipped.
                    </p>
                    {isEmail && (
                        <label className="block">
                            <span className="block text-xs font-semibold text-gray-600 mb-1">Subject</span>
                            <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)}
                                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400" />
                        </label>
                    )}
                    <label className="block">
                        <span className="block text-xs font-semibold text-gray-600 mb-1">Message</span>
                        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={isEmail ? 8 : 4}
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400" />
                    </label>

                    {isEmail && (
                        <div className="rounded-lg border border-gray-200 p-3 space-y-2.5">
                            <div className="flex items-center justify-between gap-2">
                                <span className="text-xs font-semibold text-gray-600">Image</span>
                                <button type="button" onClick={() => fileRef.current && fileRef.current.click()}
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-indigo-200 text-indigo-700 text-xs font-semibold hover:bg-indigo-50">
                                    <ImagePlus className="w-3.5 h-3.5" /> {image ? 'Replace image' : 'Add an image'}
                                </button>
                                <input ref={fileRef} type="file" accept="image/*" onChange={pickImage} className="hidden" />
                            </div>
                            {image ? (
                                <div className="flex items-center gap-2">
                                    <img src={image.dataUrl} alt={image.name}
                                        className="w-16 h-16 object-cover rounded-md border border-gray-200" />
                                    <div className="min-w-0 flex-1">
                                        <div className="text-xs text-gray-700 truncate">{image.name}</div>
                                        <div className="text-[11px] text-gray-400">{Math.round(image.size / 1024)} KB, sits under your message</div>
                                    </div>
                                    <button type="button" onClick={() => setImage(null)}
                                        className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50" title="Remove image">
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            ) : (
                                <p className="text-[11px] text-gray-400">A photo of the property, a floorplan, anything up to 3MB.</p>
                            )}
                        </div>
                    )}

                    <div className="flex items-start gap-2 text-xs text-gray-500 rounded-lg border border-gray-200 p-3">
                        <ShieldCheck className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />
                        <span>
                            {isEmail
                                ? 'An Unsubscribe button is added to the bottom of every email automatically.'
                                : 'A tap to stop unsubscribe link is added to every text automatically.'}
                        </span>
                    </div>

                    <div className="rounded-lg border border-gray-200">
                        <button type="button" onClick={() => setShowPreview((v) => !v)}
                            className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left">
                            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-700">
                                {showPreview ? <Eye className="w-3.5 h-3.5 text-indigo-600" /> : <EyeOff className="w-3.5 h-3.5 text-gray-400" />}
                                Preview {isEmail ? 'the email' : 'the text'}
                            </span>
                            {showPreview ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                        </button>
                        {showPreview && (
                            <div className="px-3 pb-3 space-y-2">
                                <p className="text-[11px] text-gray-400">
                                    Exactly what a matched renter receives. Merge tags show sample values here
                                    and are filled in per renter when it sends.
                                </p>
                                {isEmail ? (
                                    <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
                                        <div className="px-3 py-2 border-b border-gray-100 bg-gray-50">
                                            <div className="text-[10px] uppercase tracking-wide text-gray-400">Subject</div>
                                            <div className="text-xs font-semibold text-gray-800 break-words">
                                                {previewText(subject, recipients[0] && (recipients[0].name || '').split(' ')[0]) || <span className="text-gray-400">No subject yet</span>}
                                            </div>
                                        </div>
                                        <div className="p-3 max-h-72 overflow-y-auto text-sm text-gray-700 [&_a]:text-indigo-600 [&_a]:underline [&_img]:max-w-full [&_img]:rounded-lg"
                                            dangerouslySetInnerHTML={{
                                                __html: sanitizeHtml(previewHtml(emailHtml(), recipients[0] && (recipients[0].name || '').split(' ')[0])),
                                            }} />
                                    </div>
                                ) : (
                                    <div className="rounded-2xl bg-gray-100 px-3 py-2.5 text-sm text-gray-800 whitespace-pre-wrap break-words max-h-60 overflow-y-auto">
                                        {previewText(asSent(smsText()), recipients[0] && (recipients[0].name || '').split(' ')[0])}
                                    </div>
                                )}
                                <p className="text-[11px] text-gray-400">
                                    {isEmail
                                        ? 'Your message, then your image, then the Unsubscribe button. Collings branding is added around it when it sends.'
                                        : 'Your message, then the tap to stop opt-out link.'}
                                </p>
                            </div>
                        )}
                    </div>

                    {!isEmail && reachable > 0 && (
                        <p className="text-[11px] text-gray-500">
                            {sms.credits} credit{sms.credits !== 1 ? 's' : ''} per text ({sms.encoding}), about{' '}
                            <span className="font-semibold text-gray-700">{totalCredits.toLocaleString()}</span> credits for this send.
                        </p>
                    )}

                    <p className="text-[11px] text-gray-400">
                        Tip: <code className="px-1 bg-gray-100 rounded">{'{{FirstName}}'}</code> is replaced with each renter's first name,
                        and <code className="px-1 bg-gray-100 rounded">{'{{PreferencesLink}}'}</code> with a link for them to update their
                        own saved search (no login needed).
                    </p>
                </div>
                <div className="flex-shrink-0 flex items-center justify-end gap-2 px-4 py-3 border-t border-gray-100">
                    <button onClick={onClose} className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
                    <button onClick={send} disabled={sending || reachable === 0}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50">
                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        {isEmail ? 'Send emails' : 'Send texts'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function FindRentersForProperty({ initialProperty = null }) {
    const { user } = useSelector((state) => state.auth);
    const isAdmin = hasAdminAccess(user);

    const [query, setQuery] = useState('');
    const [collingsResults, setCollingsResults] = useState([]);
    const [addressResults, setAddressResults] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [searching, setSearching] = useState(false);
    const [selectedListing, setSelectedListing] = useState(null);
    const [selectedAddress, setSelectedAddress] = useState(null);

    const [filters, setFilters] = useState({
        suburb: '', state: '',
        rentMin: '', rentMax: '', bedsMin: '', bedsMax: '',
        bathsMin: '', bathsMax: '',
        propertyTypes: [], portalCategory: '', lat: null, lng: null, radiusKm: 5,
    });

    const [suburbsData, setSuburbsData] = useState(null);
    const [suburbsLoading, setSuburbsLoading] = useState(false);
    const [selectedSuburbs, setSelectedSuburbs] = useState(() => new Set());
    const [suburbFilterText, setSuburbFilterText] = useState('');
    const [showAllOther, setShowAllOther] = useState(false);
    const [suburbsOpen, setSuburbsOpen] = useState(false);
    const [suburbDistances, setSuburbDistances] = useState({});
    const OTHER_CAP = 30;

    const [results, setResults] = useState(null);
    const [resultsLoading, setResultsLoading] = useState(false);
    const [expanded, setExpanded] = useState(() => new Set());
    const [shortlist, setShortlist] = useState(() => new Set());
    const [editRenter, setEditRenter] = useState(null);
    const [messageChannel, setMessageChannel] = useState(null);
    const [visibleCount, setVisibleCount] = useState(ROWS_PER_BATCH);
    const moreRowsRef = useRef(null);

    const searchBoxRef = useRef(null);
    const searchDebounce = useRef(null);
    const suburbDebounce = useRef(null);

    useEffect(() => {
        if (searchDebounce.current) clearTimeout(searchDebounce.current);
        if (!query.trim() || query.trim().length < 2) {
            setCollingsResults([]); setAddressResults([]);
            return;
        }
        searchDebounce.current = setTimeout(async () => {
            const q = query.trim();
            try {
                setSearching(true);
                const [collings, addresses] = await Promise.all([
                    fetchFindRentersListings(q).then((d) => d.results || []).catch(() => []),
                    searchAddresses(q),
                ]);
                setCollingsResults(collings);
                setAddressResults(addresses);
                setShowDropdown(true);
            } finally {
                setSearching(false);
            }
        }, 300);
        return () => searchDebounce.current && clearTimeout(searchDebounce.current);
    }, [query]);

    useEffect(() => {
        const onClick = (e) => {
            if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, []);

    const geocodeIfNeeded = async ({ lat, lng, queryText }) => {
        if (lat != null && lng != null) return;
        if (!queryText || !hasGeocoder()) return;
        const c = await geocodePlace(queryText);
        if (c) setFilters((f) => ({ ...f, lat: c.lat, lng: c.lng }));
    };

    const pickListing = (p) => {
        setSelectedListing(p);
        setSelectedAddress(null);
        const v = (n) => (n != null ? String(n) : '');
        const listingType = LISTING_TYPE_TO_OPTION[(p.property_type || '').toLowerCase()];
        setFilters({
            suburb: p.suburb || '',
            state: p.state || '',
            rentMin: v(p.rent_min), rentMax: v(p.rent_max != null ? p.rent_max : p.rent_min),
            bedsMin: v(p.beds), bedsMax: v(p.beds),
            bathsMin: v(p.baths), bathsMax: v(p.baths),
            propertyTypes: listingType ? [listingType] : [],
            portalCategory: p.portal_category || '',
            lat: p.latitude,
            lng: p.longitude,
            radiusKm: filters.radiusKm,
        });
        setQuery('');
        setShowDropdown(false);
        setResults(null);
        geocodeIfNeeded({
            lat: p.latitude, lng: p.longitude,
            queryText: [p.address || p.suburb, p.state, 'Australia'].filter(Boolean).join(', '),
        });
    };

    const initialApplied = useRef(false);
    useEffect(() => {
        if (initialApplied.current || !initialProperty || !initialProperty.id) return;
        initialApplied.current = true;
        pickListing(initialProperty);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialProperty]);

    const pickAddress = (sugg) => {
        const f = extractAddressFields(sugg);
        setSelectedAddress({ description: sugg.description, ...f });
        setSelectedListing(null);
        setFilters((prev) => ({
            ...prev,
            suburb: f.suburb || prev.suburb,
            state: f.state || prev.state,
            portalCategory: '',
            lat: f.lat,
            lng: f.lng,
        }));
        setQuery('');
        setShowDropdown(false);
        setResults(null);
        geocodeIfNeeded({
            lat: f.lat, lng: f.lng,
            queryText: sugg.description || [f.suburb, f.state, 'Australia'].filter(Boolean).join(', '),
        });
    };

    const clearProperty = () => {
        setSelectedListing(null);
        setSelectedAddress(null);
        setFilters((f) => ({ ...f, lat: null, lng: null }));
        setResults(null);
    };

    const loadSuburbs = useCallback(async () => {
        if (!filters.suburb.trim()) { setSuburbsData(null); return; }
        try {
            setSuburbsLoading(true);
            const data = await fetchFindRentersSuburbs({
                suburb: filters.suburb.trim(),
                state: filters.state.trim(),
                lat: filters.lat,
                lng: filters.lng,
                radiusKm: filters.radiusKm,
            });

            let subs = data.suburbs || [];
            let distances = {};
            if (filters.lat != null && filters.lng != null) {
                const enriched = await enrichSuburbsWithDistance(
                    subs, { lat: filters.lat, lng: filters.lng }, filters.radiusKm, filters.state.trim()
                );
                subs = enriched.suburbs;
                distances = enriched.distances;
                if (Object.keys(distances).length) data.origin_resolved = true;
            }

            setSuburbDistances(distances);
            setSuburbsData({ ...data, suburbs: subs });
            setSelectedSuburbs(new Set(subs.filter((s) => s.recommended).map((s) => s.suburb)));
        } catch {
            setSuburbsData(null);
        } finally {
            setSuburbsLoading(false);
        }
    }, [filters.suburb, filters.state, filters.lat, filters.lng, filters.radiusKm]);

    useEffect(() => {
        if (suburbDebounce.current) clearTimeout(suburbDebounce.current);
        suburbDebounce.current = setTimeout(() => loadSuburbs(), 350);
        return () => suburbDebounce.current && clearTimeout(suburbDebounce.current);
    }, [loadSuburbs]);

    const toggleSuburb = (name) => {
        setSelectedSuburbs((prev) => {
            const next = new Set(prev);
            next.has(name) ? next.delete(name) : next.add(name);
            return next;
        });
    };

    const groupedSuburbs = useMemo(() => {
        const rows = (suburbsData?.suburbs || []).filter(
            (s) => !suburbFilterText.trim() || s.suburb.toLowerCase().includes(suburbFilterText.trim().toLowerCase())
        );
        return {
            exact: rows.filter((s) => s.kind === 'exact'),
            surrounding: rows.filter((s) => s.kind === 'surrounding'),
            other: rows.filter((s) => s.kind === 'other'),
        };
    }, [suburbsData, suburbFilterText]);

    const searchPayload = useMemo(() => {
        const num = (v) => (v !== '' && v != null ? Number(v) : null);
        return {
            property_id: selectedListing?.id,
            suburb: filters.suburb.trim(),
            state: filters.state.trim(),
            lat: filters.lat,
            lng: filters.lng,
            radius_km: filters.radiusKm,
            rent_min: num(filters.rentMin),
            rent_max: num(filters.rentMax),
            beds_min: num(filters.bedsMin),
            beds_max: num(filters.bedsMax),
            baths_min: num(filters.bathsMin),
            baths_max: num(filters.bathsMax),
            property_types: filters.propertyTypes,
            portal_category: filters.portalCategory,
            // Fall back to the typed suburb when the tick-list gave the agent
            // nothing to tick, so the panel can't dead-end on a permanently
            // disabled button. Same rule as FindBuyersForProperty
            // (CRM-2026-00040).
            suburbs: selectedSuburbs.size
                ? Array.from(selectedSuburbs)
                : [filters.suburb.trim()].filter(Boolean),
            suburb_distances: suburbDistances,
        };
    }, [selectedListing, filters, selectedSuburbs, suburbDistances]);

    const runSearch = async () => {
        if (!searchPayload.suburbs.length) {
            toast.error('Enter the suburb the property is in first.');
            return;
        }
        try {
            setResultsLoading(true);
            const data = await findRentersForProperty(searchPayload);
            setResults(data);
            setExpanded(new Set());
            setShortlist(new Set());
            setVisibleCount(ROWS_PER_BATCH);
        } catch {
            toast.error('Could not load renters. Please try again.');
        } finally {
            setResultsLoading(false);
        }
    };

    const totalRows = results?.results?.length || 0;
    useEffect(() => {
        if (visibleCount >= totalRows) return undefined;
        const el = moreRowsRef.current;
        if (!el || typeof window.IntersectionObserver !== 'function') return undefined;
        const observer = new window.IntersectionObserver((entries) => {
            if (entries.some((e) => e.isIntersecting)) {
                setVisibleCount((c) => Math.min(c + ROWS_PER_BATCH, totalRows));
            }
        }, { rootMargin: '600px' });
        observer.observe(el);
        return () => observer.disconnect();
    }, [visibleCount, totalRows]);

    const toggleShortlist = (id) => {
        setShortlist((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const shortlistRows = useMemo(
        () => (results?.results || []).filter((r) => shortlist.has(rowId(r))),
        [results, shortlist]
    );

    const messageRecipients = useMemo(
        () => (shortlistRows.length ? shortlistRows : (results?.results || [])),
        [shortlistRows, results]
    );

    const propertyLabel = useMemo(() => (
        selectedListing?.address
        || selectedAddress?.description
        || [filters.suburb, filters.state].filter(Boolean).join(', ')
        || 'this property'
    ), [selectedListing, selectedAddress, filters.suburb, filters.state]);

    const copyEmails = () => {
        const emails = shortlistRows.map((r) => r.email).filter(Boolean);
        if (!emails.length) { toast.error('No emails in the shortlist.'); return; }
        navigator.clipboard.writeText(emails.join(', '));
        toast.success(`Copied ${emails.length} email${emails.length > 1 ? 's' : ''}`);
    };

    const exportCsv = () => {
        const rows = shortlistRows.length ? shortlistRows : (results?.results || []);
        if (!rows.length) return;
        const header = ['Name', 'Email', 'Phone', 'Match %', 'Suburb match', 'Matched suburb'];
        const body = rows.map((r) => [
            r.name, r.email, r.phone, r.match_score, r.suburb_match, r.matched_suburb,
        ].map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','));
        const csv = [header.join(','), ...body].join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `renters-${(filters.suburb || 'property').toLowerCase().replace(/\s+/g, '-')}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const setFilter = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

    return (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="px-5 py-4 border-b border-gray-100">
                <div className="flex items-center gap-2">
                    <Target className="w-5 h-5 text-indigo-600" />
                    <h3 className="text-lg font-bold text-gray-900">Find Renters for a Property</h3>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                    Set the requirements for a rental (it doesn’t need to be in Collings yet)
                    and tick the suburbs that count, including renters who marked{' '}
                    <span className="font-medium">surrounding suburbs</span> or a broad area like
                    “Melbourne”.
                </p>
            </div>

            <div className="p-5 space-y-6">
                <div>
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                        1 · Property
                    </div>
                    {!selectedListing && !selectedAddress ? (
                        <div className="relative" ref={searchBoxRef}>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    onFocus={() => (collingsResults.length || addressResults.length) && setShowDropdown(true)}
                                    placeholder="Search any address, or one of your Collings lease listings…"
                                    className="w-full pl-9 pr-9 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
                                />
                                {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />}
                            </div>
                            {showDropdown && (collingsResults.length > 0 || addressResults.length > 0) && (
                                <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-auto">
                                    {collingsResults.length > 0 && (
                                        <>
                                            <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-600">In Collings</div>
                                            {collingsResults.map((p) => (
                                                <button key={`c-${p.id}`} onClick={() => pickListing(p)}
                                                    className="w-full text-left px-3 py-2.5 hover:bg-emerald-50 border-b border-gray-50 last:border-0">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-sm font-medium text-gray-900 truncate">{p.address || p.suburb}</span>
                                                        <span className="text-[10px] font-medium text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded shrink-0">In Collings</span>
                                                    </div>
                                                    <div className="text-xs text-gray-500 flex flex-wrap gap-x-3 mt-0.5">
                                                        <span>{[p.suburb, p.state].filter(Boolean).join(', ')}</span>
                                                        {p.rent_min != null && <span>{fmtMoney(p.rent_min)}/wk</span>}
                                                        {p.beds != null && <span>{p.beds} bed</span>}
                                                        {p.property_type && <span className="capitalize">{p.property_type}</span>}
                                                    </div>
                                                </button>
                                            ))}
                                        </>
                                    )}
                                    {addressResults.length > 0 && (
                                        <>
                                            <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">Other addresses (not in Collings)</div>
                                            {addressResults.map((a, i) => (
                                                <button key={`a-${i}`} onClick={() => pickAddress(a)}
                                                    className="w-full text-left px-3 py-2.5 hover:bg-indigo-50 border-b border-gray-50 last:border-0">
                                                    <div className="flex items-center gap-2">
                                                        <MapPin className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                                                        <span className="text-sm text-gray-800 truncate">{a.description}</span>
                                                    </div>
                                                </button>
                                            ))}
                                        </>
                                    )}
                                </div>
                            )}
                            <p className="text-xs text-gray-400 mt-1.5">
                                Finding renters for a new listing? Pick its address here; it doesn’t need to be in Collings yet.
                            </p>
                        </div>
                    ) : (
                        <div className="flex items-start justify-between gap-3 p-3 rounded-lg bg-indigo-50 border border-indigo-100">
                            <div className="flex items-start gap-2.5 min-w-0">
                                <Home className="w-4 h-4 text-indigo-600 mt-0.5 shrink-0" />
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-semibold text-gray-900 truncate">
                                            {selectedListing ? (selectedListing.address || selectedListing.suburb) : selectedAddress.description}
                                        </span>
                                        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded shrink-0 ${selectedListing ? 'text-emerald-700 bg-emerald-100' : 'text-gray-600 bg-gray-100'}`}>
                                            {selectedListing ? 'In Collings' : 'Not in Collings'}
                                        </span>
                                    </div>
                                    <div className="text-xs text-gray-500 flex flex-wrap gap-x-3 mt-0.5">
                                        {selectedListing ? (
                                            <>
                                                <span>{[selectedListing.suburb, selectedListing.state].filter(Boolean).join(', ')}</span>
                                                {selectedListing.rent_min != null && <span>{fmtMoney(selectedListing.rent_min)}/wk</span>}
                                                {selectedListing.beds != null && <span>{selectedListing.beds} bed</span>}
                                            </>
                                        ) : (
                                            <span>{[selectedAddress.suburb, selectedAddress.state, selectedAddress.postcode].filter(Boolean).join(', ') || 'Set the requirements below'}</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <button onClick={clearProperty} className="text-gray-400 hover:text-gray-600 shrink-0" title="Change property">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    )}
                </div>

                <div>
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                        2 · Requirements
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <label className="text-xs text-gray-500">
                            <span className="flex items-center gap-1 mb-1"><MapPin className="w-3 h-3" /> Suburb</span>
                            <SuburbAutocomplete
                                value={filters.suburb}
                                onChange={(e) => setFilter('suburb', e.target.value)}
                                onSelect={(picked) => setFilters((f) => ({
                                    ...f,
                                    suburb: picked.suburb || f.suburb,
                                    state: picked.state || f.state,
                                }))}
                                placeholder="e.g. Fawkner"
                                className="w-full px-2.5 py-2 pr-8 border border-gray-300 rounded-lg text-sm text-gray-900"
                            />
                        </label>
                        <label className="text-xs text-gray-500">
                            <span className="block mb-1">State</span>
                            <input value={filters.state} onChange={(e) => setFilter('state', e.target.value)}
                                className="w-full px-2.5 py-2 border border-gray-300 rounded-lg text-sm text-gray-900" placeholder="VIC" />
                        </label>
                        <RangeField icon={Wallet} label="Weekly rent ($)" step="10"
                            minVal={filters.rentMin} maxVal={filters.rentMax}
                            onMin={(v) => setFilter('rentMin', v)} onMax={(v) => setFilter('rentMax', v)} />
                        <RangeField icon={BedDouble} label="Bedrooms"
                            minVal={filters.bedsMin} maxVal={filters.bedsMax}
                            onMin={(v) => setFilter('bedsMin', v)} onMax={(v) => setFilter('bedsMax', v)} />
                        <RangeField icon={Bath} label="Bathrooms"
                            minVal={filters.bathsMin} maxVal={filters.bathsMax}
                            onMin={(v) => setFilter('bathsMin', v)} onMax={(v) => setFilter('bathsMax', v)} />
                        <label className="text-xs text-gray-500">
                            <span className="flex items-center gap-1 mb-1"><Compass className="w-3 h-3" /> Radius</span>
                            <select value={filters.radiusKm} onChange={(e) => setFilter('radiusKm', Number(e.target.value))}
                                className="w-full px-2.5 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 bg-white">
                                {RADIUS_OPTIONS.map((r) => <option key={r} value={r}>{r} km</option>)}
                            </select>
                        </label>
                    </div>
                    <div className="mt-3">
                        <span className="flex items-center gap-1 mb-1.5 text-xs text-gray-500"><Building2 className="w-3 h-3" /> Type</span>
                        <PropertyTypeTiles value={filters.propertyTypes} onChange={(v) => setFilter('propertyTypes', v)} accent="indigo" />
                        <p className="text-xs text-gray-400 mt-1.5">Tick as many as apply, e.g. Apartment, Unit and Villa together — leave none ticked to match any type.</p>
                    </div>
                    <p className="text-xs text-gray-400 mt-1.5">Leave a field blank to ignore it. Rent matching uses a 20% range: renters whose stated rent is more than 20% away from this listing's rent are left out. (Renters who never set a rent budget still show.)</p>
                </div>

                <div>
                    <button type="button" onClick={() => setSuburbsOpen((o) => !o)}
                        className="w-full flex items-center justify-between mb-1 group">
                        <div className="flex items-center gap-1.5">
                            {suburbsOpen ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">3 · Suburbs renters have marked</span>
                        </div>
                        <span className="text-xs text-gray-400">
                            {!filters.suburb.trim() ? 'enter a suburb'
                                : suburbsLoading ? 'loading…'
                                    : `${selectedSuburbs.size} ticked${!suburbsOpen ? ' · click to choose' : ''}`}
                        </span>
                    </button>

                    {!suburbsOpen && selectedSuburbs.size > 0 && (
                        <div className="text-xs text-gray-500 mb-1 pl-5 truncate">
                            {Array.from(selectedSuburbs).slice(0, 6).join(', ')}
                            {selectedSuburbs.size > 6 ? ` +${selectedSuburbs.size - 6} more` : ''}
                        </div>
                    )}

                    {suburbsOpen && (!filters.suburb.trim() ? (
                        <div className="text-sm text-gray-400 italic py-3">Enter a suburb above to see who’s looking nearby.</div>
                    ) : suburbsLoading ? (
                        <div className="flex items-center gap-2 text-sm text-gray-400 py-3"><Loader2 className="w-4 h-4 animate-spin" /> Loading suburbs…</div>
                    ) : !suburbsData || !(suburbsData.suburbs || []).length ? (
                        <div className="text-sm text-gray-400 italic py-3">
                            No renters have marked any suburbs yet. Searching <strong>{filters.suburb.trim()}</strong> on its own.
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {!suburbsData.origin_resolved && (
                                <div className="text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                                    We couldn’t find map coordinates for this suburb, so nothing is auto-ticked by distance. Tick the relevant areas by hand below.
                                </div>
                            )}
                            {(suburbsData.suburbs || []).length > 8 && (
                                <input value={suburbFilterText} onChange={(e) => setSuburbFilterText(e.target.value)}
                                    placeholder="Filter suburbs…"
                                    className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm" />
                            )}
                            {['exact', 'surrounding', 'other'].map((kind) => {
                                const allRows = groupedSuburbs[kind];
                                if (!allRows.length) return null;
                                const meta = SUBURB_KIND[kind];
                                const capped = kind === 'other' && !showAllOther && !suburbFilterText.trim();
                                const rows = capped ? allRows.slice(0, OTHER_CAP) : allRows;
                                return (
                                    <div key={kind}>
                                        <div className="flex items-center gap-2 mb-1.5">
                                            <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded ${meta.chip}`}>{meta.label}</span>
                                            {kind === 'other' && <span className="text-[11px] text-gray-400">renters who marked a broader area (e.g. just “Melbourne”), sorted by demand</span>}
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            {rows.map((s) => {
                                                const on = selectedSuburbs.has(s.suburb);
                                                return (
                                                    <button key={s.suburb} onClick={() => toggleSuburb(s.suburb)}
                                                        className={`inline-flex items-center gap-1.5 pl-2 pr-2.5 py-1.5 rounded-lg border text-sm transition ${on ? 'bg-indigo-50 border-indigo-300 text-indigo-800' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                                                        <span className={`w-4 h-4 rounded flex items-center justify-center border ${on ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300'}`}>
                                                            {on && <Check className="w-3 h-3 text-white" />}
                                                        </span>
                                                        <span className="font-medium">{s.suburb}</span>
                                                        <span className="text-xs text-gray-400">
                                                            {s.interstate && s.state ? `${s.state} · ` : ''}
                                                            {s.distance_km != null ? `${s.distance_km}km · ` : ''}{s.buyer_count} renter{s.buyer_count !== 1 ? 's' : ''}
                                                        </span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        {capped && allRows.length > OTHER_CAP && (
                                            <button onClick={() => setShowAllOther(true)}
                                                className="mt-2 text-xs font-medium text-indigo-600 hover:text-indigo-700">
                                                Show all {allRows.length} areas
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>

                <div className="flex flex-wrap items-center gap-3 pt-1">
                    <button onClick={runSearch} disabled={resultsLoading || !searchPayload.suburbs.length}
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed">
                        {resultsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />}
                        Find renters
                    </button>
                    <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
                        <Check className="w-3.5 h-3.5 text-emerald-600" />
                        Only renters who meet every criterion you set are shown. Leave a field blank to ignore it.
                    </span>
                </div>
            </div>

            {results && (
                <div className="border-t border-gray-100 p-5">
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                        <div className="text-sm text-gray-700">
                            <span className="font-bold text-gray-900">{results.count.toLocaleString()}</span> potential renter{results.count !== 1 ? 's' : ''}
                            {results.count > 0 && ' · best fit first'}
                            {visibleCount < totalRows && (
                                <span className="text-gray-400"> · showing {visibleCount.toLocaleString()}, scroll for more</span>
                            )}
                        </div>
                        {results.count > 0 && (
                            <div className="flex flex-wrap items-center gap-2">
                                {shortlist.size > 0 && (
                                    <>
                                        <span className="text-xs text-gray-500">{shortlist.size} selected</span>
                                        <button onClick={copyEmails} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-700 hover:bg-gray-50">
                                            <Copy className="w-3.5 h-3.5" /> Copy emails
                                        </button>
                                    </>
                                )}
                                <button onClick={() => setMessageChannel('email')}
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-semibold hover:bg-indigo-700">
                                    <Mail className="w-3.5 h-3.5" /> Email {shortlist.size > 0 ? 'selected' : 'all'}
                                </button>
                                <button onClick={() => setMessageChannel('sms')}
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-indigo-200 text-indigo-700 text-xs font-semibold hover:bg-indigo-50">
                                    <MessageSquare className="w-3.5 h-3.5" /> Text {shortlist.size > 0 ? 'selected' : 'all'}
                                </button>
                                {isAdmin && (
                                    <button onClick={exportCsv} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-700 hover:bg-gray-50">
                                        <Download className="w-3.5 h-3.5" /> Export CSV
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    {results.count === 0 ? (
                        <div className="text-sm text-gray-500 py-6 text-center">
                            No renters meet every criterion. Try widening a range (rent, beds, baths), ticking more suburbs, or leaving a field blank to ignore it.
                        </div>
                    ) : (
                        <div className="space-y-2.5">
                            {results.results.slice(0, visibleCount).map((r) => {
                                const meta = MATCH_SUBURB_META[r.suburb_match] || MATCH_SUBURB_META.other;
                                const rid = rowId(r);
                                const isOpen = expanded.has(rid);
                                return (
                                    <div key={rid} className={`rounded-xl border ${meta.ring} ${meta.bg}`}>
                                        <div className="flex flex-wrap sm:flex-nowrap items-center gap-3 p-3">
                                            <input type="checkbox" checked={shortlist.has(rid)} onChange={() => toggleShortlist(rid)}
                                                className="rounded border-gray-300 shrink-0" onClick={(e) => e.stopPropagation()} />
                                            <div className="text-center shrink-0 w-12">
                                                <div className={`text-xl font-extrabold leading-none ${scoreColor(r.match_score)}`}>{r.match_score}</div>
                                                <div className="text-[10px] text-gray-400 uppercase tracking-wide">match</div>
                                            </div>
                                            <button onClick={() => setExpanded((prev) => { const n = new Set(prev); n.has(rid) ? n.delete(rid) : n.add(rid); return n; })}
                                                className="flex-1 min-w-0 text-left">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="text-sm font-semibold text-gray-900 truncate">{r.name || r.email || 'Renter'}</span>
                                                    <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded ${meta.text} bg-white/70`}>
                                                        {r.matched_suburb}{r.matched_distance_km != null && r.suburb_match === 'surrounding' ? ` · ${r.matched_distance_km}km` : ''}
                                                    </span>
                                                    {r.latest_feedback && fbBucket(r.latest_feedback) && (
                                                        <span
                                                            title={r.latest_feedback.text || 'Open-home feedback'}
                                                            className={`inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded ${FB_META[fbBucket(r.latest_feedback)].cls}`}
                                                        >
                                                            <MessageSquare className="w-3 h-3" /> {FB_META[fbBucket(r.latest_feedback)].label}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="flex flex-wrap items-center gap-1.5 mt-1">
                                                    <FitChip ok={r.rent_fit === 'in'} near={r.rent_fit === 'near'} label={r.rent_fit === 'out' ? 'Rent off' : 'Rent'} />
                                                    <FitChip ok={r.beds_fit === 'in'} near={r.beds_fit === 'near'} label={r.beds_fit === 'out' ? 'Beds off' : 'Beds'} />
                                                    <FitChip ok={r.baths_fit === 'in'} near={r.baths_fit === 'near'} label={r.baths_fit === 'out' ? 'Baths off' : 'Baths'} />
                                                    <FitChip ok={r.type_fit === 'in'} near={false} label={r.type_fit === 'out' ? 'Type differs' : 'Type'} />
                                                </div>
                                            </button>
                                            <div className="flex w-full sm:w-auto justify-end items-center gap-1.5 shrink-0">
                                                {r.phone && (
                                                    <a href={`tel:${r.phone}`} onClick={(e) => e.stopPropagation()} title={r.phone}
                                                        className="p-2 rounded-lg bg-white border border-gray-200 text-gray-600 hover:text-indigo-600 hover:border-indigo-200">
                                                        <Phone className="w-4 h-4" />
                                                    </a>
                                                )}
                                                {r.email && (
                                                    <a href={`mailto:${r.email}`} onClick={(e) => e.stopPropagation()} title={r.email}
                                                        className="p-2 rounded-lg bg-white border border-gray-200 text-gray-600 hover:text-indigo-600 hover:border-indigo-200">
                                                        <Mail className="w-4 h-4" />
                                                    </a>
                                                )}
                                                <button onClick={(e) => { e.stopPropagation(); setEditRenter(r); }} title="Edit preferences"
                                                    className="p-2 rounded-lg bg-white border border-gray-200 text-gray-600 hover:text-indigo-600 hover:border-indigo-200">
                                                    <Pencil className="w-4 h-4" />
                                                </button>
                                                <button onClick={() => setExpanded((prev) => { const n = new Set(prev); n.has(rid) ? n.delete(rid) : n.add(rid); return n; })}
                                                    className="p-2 rounded-lg text-gray-400 hover:text-gray-600">
                                                    {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                                </button>
                                            </div>
                                        </div>
                                        {isOpen && (
                                            <div className="px-3 pb-3 pt-0 text-xs text-gray-600 border-t border-white/60 bg-white/40">
                                                <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 pt-2">
                                                    <div><span className="text-gray-400">Wants suburbs:</span> {(r.pref_suburbs || []).join(', ') || '-'}</div>
                                                    <div><span className="text-gray-400">Weekly rent:</span> {fmtMoney(r.pref_rent_min) || 'Any'} to {fmtMoney(r.pref_rent_max) || 'Any'}</div>
                                                    <div><span className="text-gray-400">Bedrooms:</span> {r.pref_bedrooms_min || 'Any'} to {r.pref_bedrooms_max || 'Any'}</div>
                                                    <div><span className="text-gray-400">Bathrooms:</span> {r.pref_bathrooms || 'Any'}</div>
                                                    <div><span className="text-gray-400">Type:</span> {(r.pref_property_type || []).join(', ') || 'Any'}</div>
                                                </div>
                                                {r.latest_feedback && (r.latest_feedback.text || r.latest_feedback.price_feedback) && (
                                                    <div className="mt-2 rounded-md bg-teal-50 border border-teal-100 px-2 py-1.5">
                                                        <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-teal-700">
                                                            <MessageSquare className="w-3 h-3" /> Open-home feedback
                                                            {r.latest_feedback.property && <span className="text-gray-500 font-normal normal-case">· {r.latest_feedback.property}</span>}
                                                        </div>
                                                        {r.latest_feedback.text && <p className="text-[11px] text-gray-700 mt-1 whitespace-pre-wrap break-words">"{r.latest_feedback.text}"</p>}
                                                        {r.latest_feedback.price_feedback && <p className="text-[10px] text-gray-500 mt-0.5">Rent feedback: {r.latest_feedback.price_feedback}</p>}
                                                    </div>
                                                )}
                                                <div className="mt-2 text-gray-500">{(r.reasons || []).join(' · ')}</div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                            {visibleCount < totalRows && (
                                <div ref={moreRowsRef} className="pt-2 text-center">
                                    <button
                                        onClick={() => setVisibleCount((c) => Math.min(c + ROWS_PER_BATCH, totalRows))}
                                        className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-gray-50">
                                        Show more ({(totalRows - visibleCount).toLocaleString()} still to load)
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {editRenter && (
                <EditRenterModal
                    renter={editRenter}
                    onClose={() => setEditRenter(null)}
                    onSaved={() => { setEditRenter(null); runSearch(); }}
                />
            )}

            {messageChannel && (
                <MessageRenterMatchesModal
                    channel={messageChannel}
                    recipients={messageRecipients}
                    propertyLabel={propertyLabel}
                    onClose={() => setMessageChannel(null)}
                    onSent={() => setMessageChannel(null)}
                />
            )}
        </div>
    );
}
